import * as React from "react";

import { SelectChangeEvent } from "@mui/material/Select";
import {
  AvailablePaymentCurrency,
  CommandFormEmbedVersion,
  DonationFormCategory,
  FeatureFlagValue,
  FormType,
  Locales,
  PaymentMethod,
  StripeRecurringInterval,
  TicketingFormCategory,
  Undefinable,
  Emptyable,
} from "@simplyk/common";

import { PaymentMode } from "@/common/helpers/stripe";
import {
  TipSuggestionOutput,
  DonationFormFrontendPageOutput,
  FormOrganizationOutput,
  TicketingFrontendPageOutput,
} from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SelectedTip } from "@/components/PaymentProcessor/hooks/useComputeTip";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { AmplitudeContext } from "@/components/PaymentProcessor/type";

export interface FrontendFormContextValues {
  formData: DonationFormFrontendPageOutput | TicketingFrontendPageOutput;
  organization: FormOrganizationOutput;
  commandId: string;
  dafExposureAmount?: { current: number | null };
  dafFlow: { token: string; isResume: boolean; emailUpdateToken?: string } | null;
  setDafFlow: (flow: { token: string; isResume: boolean; emailUpdateToken?: string } | null) => void;
  formType: FormType;
  category: TicketingFormCategory | DonationFormCategory;
  themeColor?: Emptyable<string>;
  translationLocales: Locales[]; // Languages the form can be translated to, excluding the current language.
  isOnboarding: boolean;
  isEmbed: boolean;
  isEmbedV2: boolean;
  formEmbedVersion: CommandFormEmbedVersion | null;
  embedId: string | null;
  formAmplitudeContext: AmplitudeContext;
  selectedPaymentMethod: Undefinable<PaymentMethod>;
  setSelectedPaymentMethod: (paymentMethod: Undefinable<PaymentMethod>, complete?: boolean) => void;
  currency: AvailablePaymentCurrency | undefined;
  displayedFormAmount: number;
  setDisplayedFormAmount: React.Dispatch<React.SetStateAction<number>>;
  displayedFormTaxAmount: number;
  setDisplayedFormTaxAmount: React.Dispatch<React.SetStateAction<number>>;
  tipBaseAmount: number;
  setShouldFetchTipSuggestions: React.Dispatch<React.SetStateAction<boolean>>;
  displayedFormAmountWithTip: number;
  tip: number;
  suggestedTips: TipSuggestionOutput[] | undefined;
  defaultTipSuggestion: TipSuggestionOutput | undefined;
  selectedTip: SelectedTip;
  isSuggestedTipsAmount: boolean;
  handleChangeTips: (event: SelectChangeEvent<unknown>) => void;
  handleChangeFreeTips: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  freeTips: number | "";
  updateFreeTipAmount: (amount: number) => void;
  fees: number;
  formattedFees: string | undefined;
  displayTipSelection: boolean;
  stripeRecurrenceInterval: Undefinable<StripeRecurringInterval | null>;
  setStripeRecurrenceInterval: React.Dispatch<React.SetStateAction<StripeRecurringInterval | null>>;
  isMembershipV2: boolean;
  isArchived: boolean;
  isDraft?: boolean;
  areBankPaymentMethodsAllowed: boolean;
  allowedPaymentMethods: PaymentMethod[];
  postTransactionUrl: string | null | undefined;
  prioritizeBankPayment: boolean;
  paymentStepExperiment: { name: string; value: FeatureFlagValue } | null;
  markAmountChangedByDonor: () => void;
  collapseRecommendedBankPayment?: boolean;
  selectedPaymentMethodIsBank: boolean;
  selectedPaymentMethodIsCard: boolean;
  chequeDescription: string | null | undefined;
  showProceedButton: boolean;
  isExpressCheckoutLoading: boolean;
  supportsExpressCheckout: boolean;
  displayExpressCheckout: boolean;
  isCashAppEnabled: boolean;
  handleSetSupportsExpressCheckout: (value: boolean) => void;
  isSubmitting: boolean;
  setIsSubmitting: (value: boolean) => void;
  expressCheckoutIsSelected: boolean;
  expressCheckoutDisabled: boolean;
  paymentMode: PaymentMode;
  isAuction: boolean;
  shouldSendTipPercentage: boolean;
  getTipChoice: (value: string) => number | "custom";
  logAmplitudeTipsChanged: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  displayAddressQuestion: boolean;
  isPreviewTemplateMode?: boolean;
  isPreview?: boolean;
  generateETicket: boolean;
  defaultSelectedPaymentMethod: PaymentMethod | undefined;
  isFreeTicketing: boolean;
  isPaymentDisabled?: boolean;
  isPadLoading: boolean;
  setIsPadLoading: (value: boolean) => void;
  tipExperiment: { name: string; value: FeatureFlagValue } | null;
}

const defaultValues = {
  formData: {} as DonationFormFrontendPageOutput,
  organization: {} as FormOrganizationOutput,
  commandId: "",
  dafFlow: null,
  setDafFlow: () => {},
  formType: FormType.DonationForm,
  category: DonationFormCategory.DonationForm,
  themeColor: "",
  translationLocales: [],
  isOnboarding: false,
  isEmbed: false,
  isEmbedV2: false,
  formEmbedVersion: null,
  embedId: null,
  formAmplitudeContext: {} as never,
  selectedPaymentMethod: undefined,
  selectedPaymentMethodIsComplete: false,
  setSelectedPaymentMethod: () => {},
  currency: undefined,
  amplitudeContext: undefined,
  displayedFormAmount: 0,
  setDisplayedFormAmount: () => {},
  displayedFormTaxAmount: 0,
  setDisplayedFormTaxAmount: () => {},
  tipBaseAmount: 0,
  setShouldFetchTipSuggestions: () => {},
  displayedFormAmountWithTip: 0,
  tip: 0,
  suggestedTips: undefined,
  defaultTipSuggestion: undefined,
  selectedTip: {},
  isSuggestedTipsAmount: false,
  handleChangeTips: () => {},
  handleChangeFreeTips: () => {},
  freeTips: "" as const,
  updateFreeTipAmount: () => {},
  fees: 0,
  formattedFees: undefined,
  displayTipSelection: true,
  stripeRecurrenceInterval: undefined,
  setStripeRecurrenceInterval: () => {},
  isMembershipV2: false,
  isArchived: false,
  isDraft: false,
  areBankPaymentMethodsAllowed: false,
  allowedPaymentMethods: [],
  postTransactionUrl: null,
  prioritizeBankPayment: false,
  paymentStepExperiment: null,
  markAmountChangedByDonor: () => undefined,
  collapseRecommendedBankPayment: false,
  selectedPaymentMethodIsBank: false,
  selectedPaymentMethodIsCard: false,
  chequeDescription: null,
  showProceedButton: false,
  displayExpressCheckout: false,
  isCashAppEnabled: false,
  supportsExpressCheckout: false,
  handleSetSupportsExpressCheckout: () => {},
  isExpressCheckoutLoading: false,
  isSubmitting: false,
  setIsSubmitting: () => {},
  expressCheckoutIsSelected: false,
  expressCheckoutDisabled: false,
  paymentMode: "payment" as const,
  isAuction: false,
  shouldSendTipPercentage: false,
  getTipChoice: () => 0,
  logAmplitudeTipsChanged: () => {},
  displayAddressQuestion: false,
  isPreviewTemplateMode: false,
  isPreview: false,
  generateETicket: false,
  defaultSelectedPaymentMethod: undefined,
  isFreeTicketing: false,
  isPaymentDisabled: false,
  isPadLoading: false,
  setIsPadLoading: () => {},
  tipExperiment: null,
};

export const FrontendFormContext = React.createContext<FrontendFormContextValues>(defaultValues);
